import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusPill from "@/components/StatusPill";
import Checklist from "@/components/Checklist";
import { Field, SummaryBar, DataTable } from "@/pages/FridgeLogs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Plus, Trash2, Pencil, Sparkles, FileDown, LayoutTemplate, X, RotateCcw, Download, FileText } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const empty = {
  title: "",
  area: "Premises",
  score: 90,
  status: "open",
  findings: "",
  corrective_actions: "",
  audit_date: new Date().toISOString().slice(0, 10),
  due_date: "",
  template: "",
  checklist: [],
};

const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 64);

export default function Audits() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [tplManagerOpen, setTplManagerOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  const load = async () => setItems((await api.get("/audits")).data);
  const loadTemplates = async () => {
    try { setTemplates((await api.get("/audits/templates")).data); } catch { setTemplates([]); }
  };
  useEffect(() => { load(); loadTemplates(); }, []);

  const openCreate = () => { setEditingId(null); setForm(empty); setOpen(true); loadTemplates(); };
  const openEdit = (r) => {
    setEditingId(r.id);
    setForm({
      title: r.title,
      area: r.area,
      score: r.score,
      status: r.status,
      findings: r.findings || "",
      corrective_actions: r.corrective_actions || "",
      audit_date: r.audit_date,
      due_date: r.due_date || "",
      template: r.template || "",
      checklist: r.checklist || [],
    });
    setOpen(true);
    loadTemplates();
  };

  const applyTemplate = (key) => {
    const tpl = templates.find((t) => t.key === key);
    if (!tpl) return;
    setForm((f) => {
      const titleEmpty = !f.title.trim();
      const existingTexts = new Set(f.checklist.map((c) => (c.text || "").trim().toLowerCase()));
      const additions = tpl.items.filter((it) => !existingTexts.has(it.text.trim().toLowerCase()));
      return {
        ...f,
        template: key,
        title: titleEmpty ? `${tpl.label} audit — ${f.audit_date}` : f.title,
        checklist: [...f.checklist, ...additions],
      };
    });
    toast.success(`Loaded "${tpl.label}" template`);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        ...form,
        score: Number(form.score),
        due_date: form.due_date || null,
        checklist: form.checklist.filter((c) => (c.text || "").trim()),
      };
      if (editingId) {
        await api.put(`/audits/${editingId}`, body);
        toast.success("Audit updated");
      } else {
        await api.post("/audits", body);
        toast.success("Audit recorded");
      }
      setOpen(false);
      setEditingId(null);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const del = async (id) => { await api.delete(`/audits/${id}`); toast.success("Audit deleted"); load(); };

  const downloadPdf = async (row) => {
    try {
      const res = await api.get(`/audits/${row.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safe = (row.title || "audit").replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 48) || "audit";
      a.href = url;
      a.download = `audit-${safe}-${row.audit_date}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "PDF download failed");
    }
  };

  const openCount = items.filter((i) => i.status === "open").length;
  const avgScore = items.length ? Math.round(items.reduce((a, b) => a + b.score, 0) / items.length) : 0;

  const checklistDone = form.checklist.filter((c) => c.done).length;
  const checklistTotal = form.checklist.length;
  const computedScore = checklistTotal ? Math.round((checklistDone / checklistTotal) * 100) : null;

  return (
    <div className="p-4 sm:p-8 lg:p-12 max-w-[1400px]" data-testid="audits-page">
      <PageHeader
        eyebrow="Health & Safety"
        title="Audit dashboard"
        description="Track inspections, scores and outstanding corrective actions across every site."
        actions={
          <div className="flex items-center gap-2">
            <Button
              data-testid="audit-export-button"
              variant="outline"
              onClick={() => setExportOpen(true)}
              className="border-[#1b3b36]/30 text-[#1b3b36] hover:bg-[#e5efe8] rounded-[4px] h-10 px-4"
            >
              <Download className="w-4 h-4 mr-2" /> Export
            </Button>
            {isOwner && (
              <Button
                data-testid="audit-manage-templates-button"
                variant="outline"
                onClick={() => setTplManagerOpen(true)}
                className="border-[#1b3b36]/30 text-[#1b3b36] hover:bg-[#e5efe8] rounded-[4px] h-10 px-4"
              >
                <LayoutTemplate className="w-4 h-4 mr-2" /> Manage templates
              </Button>
            )}
            <Button data-testid="audit-add-button" onClick={openCreate} className="bg-[#1b3b36] hover:bg-[#2c5a53] text-white rounded-[4px] h-10 px-5">
              <Plus className="w-4 h-4 mr-2" /> New audit
            </Button>
          </div>
        }
      />

      <SummaryBar items={[
        { label: "Total audits", value: items.length, testid: "stat-total-audits" },
        { label: "Open", value: openCount, tone: openCount ? "alert" : "success", testid: "stat-open-audits" },
        { label: "Average score", value: `${avgScore}`, testid: "stat-avg-score" },
      ]} />

      <DataTable
        empty="No audits recorded yet. Start your first inspection."
        head={["Title", "Area", "Date", "Score", "Checks", "Status", ""]}
        rows={items}
        testid="audits-table"
        render={(i, idx) => {
          const cl = Array.isArray(i.checklist) ? i.checklist : [];
          const done = cl.filter((c) => c.done).length;
          return (
            <tr key={i.id} data-testid={`audit-row-${idx}`} className="border-t border-[#e5e2d9] hover:bg-[#efece6]/40 align-top">
              <td className="px-4 py-3 text-sm">
                <div className="font-medium">{i.title}</div>
                {i.template && (
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#8a8476] mt-0.5">
                    {(templates.find((t) => t.key === i.template)?.label) || i.template}
                  </div>
                )}
              </td>
              <td className="px-4 py-3 text-sm">{i.area}</td>
              <td className="px-4 py-3 text-sm whitespace-nowrap">{i.audit_date}</td>
              <td className="px-4 py-3 text-sm font-mono">{i.score}</td>
              <td className="px-4 py-3 text-sm font-mono text-[#57534e]" data-testid={`audit-checks-${idx}`}>
                {cl.length ? `${done} / ${cl.length}` : "—"}
              </td>
              <td className="px-4 py-3">
                <StatusPill tone={i.status === "passed" ? "success" : i.status === "failed" ? "alert" : "warning"}>{i.status}</StatusPill>
              </td>
              <td className="px-4 py-3 text-right">
                <div className="inline-flex items-center gap-3">
                  <button data-testid={`audit-pdf-${idx}`} onClick={() => downloadPdf(i)} className="text-[#8a8476] hover:text-[#1b3b36]" aria-label="Download PDF" title="Download PDF"><FileDown className="w-4 h-4" /></button>
                  <button data-testid={`audit-edit-${idx}`} onClick={() => openEdit(i)} className="text-[#8a8476] hover:text-[#1b3b36]" aria-label="Edit"><Pencil className="w-4 h-4" /></button>
                  <button data-testid={`audit-delete-${idx}`} onClick={() => del(i.id)} className="text-[#8a8476] hover:text-[#d94f3d]" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                </div>
              </td>
            </tr>
          );
        }}
      />

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(empty); } }}>
        <DialogContent className="bg-white max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editingId ? "Edit audit" : "New audit"}</DialogTitle><DialogDescription className="sr-only">Record an inspection with checks, score, and corrective actions.</DialogDescription></DialogHeader>
          <form className="space-y-4" onSubmit={submit} data-testid="audit-form">
            <Field label="Template">
              <Select value={form.template || "__custom__"} onValueChange={(v) => v === "__custom__" ? setForm((f) => ({ ...f, template: "" })) : applyTemplate(v)}>
                <SelectTrigger data-testid="audit-template-select" className="bg-white rounded-[4px]">
                  <SelectValue placeholder="Custom audit (start blank)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__custom__" data-testid="audit-template-custom">
                    <span className="text-[#8a8476] italic">Custom audit (start blank)</span>
                  </SelectItem>
                  {templates.map((t) => (
                    <SelectItem key={t.key} value={t.key} data-testid={`audit-template-${t.key}`}>
                      <span className="inline-flex items-center gap-2">
                        <Sparkles className="w-3 h-3 text-[#4e7b5b]" />
                        {t.label}
                        <span className="text-[10px] text-[#8a8476]">({t.items.length} checks)</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Title">
              <Input required data-testid="audit-title-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="bg-white rounded-[4px]" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Area">
                <Input data-testid="audit-area-input" value={form.area} onChange={(e) => setForm((f) => ({ ...f, area: e.target.value }))} className="bg-white rounded-[4px]" placeholder="Kitchen, shop floor, storage…" />
              </Field>
              <Field label="Score (0–100)">
                <div className="flex items-center gap-2">
                  <Input type="number" min="0" max="100" data-testid="audit-score-input" value={form.score} onChange={(e) => setForm((f) => ({ ...f, score: e.target.value }))} className="bg-white rounded-[4px]" />
                  {computedScore !== null && (
                    <button
                      type="button"
                      data-testid="audit-score-use-checks"
                      onClick={() => setForm((f) => ({ ...f, score: computedScore }))}
                      className="text-[10px] uppercase tracking-[0.18em] text-[#1b3b36] hover:underline whitespace-nowrap"
                      title="Use score derived from checks"
                    >
                      use {computedScore}
                    </button>
                  )}
                </div>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Audit date"><Input type="date" data-testid="audit-date-input" value={form.audit_date} onChange={(e) => setForm((f) => ({ ...f, audit_date: e.target.value }))} className="bg-white rounded-[4px]" /></Field>
              <Field label="Due date"><Input type="date" data-testid="audit-due-input" value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} className="bg-white rounded-[4px]" /></Field>
            </div>
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="audit-status-select" className="bg-white rounded-[4px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="passed">Passed</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            <div className="border-t border-[#e5e2d9] pt-4" data-testid="audit-checklist-wrap">
              <Checklist
                value={form.checklist}
                onChange={(next) => setForm((f) => ({ ...f, checklist: next }))}
                title="Inspection checks"
                emptyHint="No checks yet. Pick a template above, or add items manually."
                promptNoteOnFail
              />
            </div>

            <Field label="Findings"><Textarea data-testid="audit-findings-input" value={form.findings} onChange={(e) => setForm((f) => ({ ...f, findings: e.target.value }))} className="bg-white rounded-[4px]" /></Field>
            <Field label="Corrective actions"><Textarea data-testid="audit-corrective-input" value={form.corrective_actions} onChange={(e) => setForm((f) => ({ ...f, corrective_actions: e.target.value }))} className="bg-white rounded-[4px]" /></Field>

            <DialogFooter>
              <Button type="submit" disabled={busy} data-testid="audit-submit-button" className="bg-[#1b3b36] hover:bg-[#2c5a53] text-white rounded-[4px]">
                {busy ? "Saving…" : editingId ? "Save changes" : "Save audit"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <TemplateManager
        open={tplManagerOpen}
        onOpenChange={setTplManagerOpen}
        templates={templates}
        reload={loadTemplates}
      />

      <ExportDialog open={exportOpen} onOpenChange={setExportOpen} />
    </div>
  );
}

function ExportDialog({ open, onOpenChange }) {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [busy, setBusy] = useState(null); // "csv" | "pdf" | null

  const buildUrl = (path) => {
    const params = new URLSearchParams();
    if (startDate) params.set("start_date", startDate);
    if (endDate) params.set("end_date", endDate);
    const qs = params.toString();
    return qs ? `${path}?${qs}` : path;
  };

  const download = async (fmt) => {
    const path = fmt === "csv" ? "/audits/export.csv" : "/audits/export.pdf";
    const mime = fmt === "csv" ? "text/csv" : "application/pdf";
    const today = new Date().toISOString().slice(0, 10);
    const fname = fmt === "csv"
      ? `audits-export-${today}.csv`
      : `audit-compliance-bundle-${today}.pdf`;
    setBusy(fmt);
    try {
      const res = await api.get(buildUrl(path), { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(fmt === "csv" ? "CSV downloaded" : "Compliance bundle downloaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Export failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-lg" data-testid="audit-export-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Export audit records</DialogTitle>
          <DialogDescription className="text-sm text-[#57534e]">
            EHO-ready compliance exports. Leave dates blank to include every audit on record.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="From">
              <Input
                type="date"
                data-testid="audit-export-start-date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="bg-white rounded-[4px]"
              />
            </Field>
            <Field label="To">
              <Input
                type="date"
                data-testid="audit-export-end-date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="bg-white rounded-[4px]"
              />
            </Field>
          </div>
          <div className="border border-[#e5e2d9] divide-y divide-[#efece6]">
            <button
              type="button"
              data-testid="audit-export-csv-button"
              disabled={busy !== null}
              onClick={() => download("csv")}
              className="w-full px-4 py-3 flex items-center gap-3 hover:bg-[#efece6]/40 text-left disabled:opacity-60"
            >
              <FileText className="w-4 h-4 text-[#1b3b36] flex-shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-medium">Spreadsheet (CSV)</div>
                <div className="text-[11px] text-[#8a8476]">One row per audit with checks summary, failed items and corrective actions. Opens in Excel/Sheets.</div>
              </div>
              {busy === "csv" && <span className="text-[10px] uppercase tracking-[0.18em] text-[#1b3b36]">…</span>}
            </button>
            <button
              type="button"
              data-testid="audit-export-pdf-button"
              disabled={busy !== null}
              onClick={() => download("pdf")}
              className="w-full px-4 py-3 flex items-center gap-3 hover:bg-[#efece6]/40 text-left disabled:opacity-60"
            >
              <FileDown className="w-4 h-4 text-[#1b3b36] flex-shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-medium">Compliance bundle (PDF)</div>
                <div className="text-[11px] text-[#8a8476]">Cover page + index + one signed page per audit. Hand to your EHO during a visit.</div>
              </div>
              {busy === "pdf" && <span className="text-[10px] uppercase tracking-[0.18em] text-[#1b3b36]">…</span>}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TemplateManager({ open, onOpenChange, templates, reload }) {
  const blank = { mode: "create", key: "", label: "", items: [""], originalKey: "" };
  const [editor, setEditor] = useState(null); // null = list view, object = editor
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!open) setEditor(null); }, [open]);

  const startCreate = () => setEditor({ ...blank });
  const startEdit = (tpl) => setEditor({
    mode: "edit",
    key: tpl.key,
    originalKey: tpl.key,
    label: tpl.label,
    items: (tpl.items || []).map((i) => i.text || ""),
    is_default: tpl.is_default,
  });

  const setItem = (idx, val) => setEditor((e) => {
    const next = e.items.slice();
    next[idx] = val;
    return { ...e, items: next };
  });
  const addItem = () => setEditor((e) => ({ ...e, items: [...e.items, ""] }));
  const removeItem = (idx) => setEditor((e) => {
    const next = e.items.slice();
    next.splice(idx, 1);
    return { ...e, items: next.length ? next : [""] };
  });

  const save = async () => {
    if (!editor) return;
    const label = editor.label.trim();
    if (!label) return toast.error("Template name is required");
    const items = editor.items.map((s) => s.trim()).filter(Boolean);
    if (items.length === 0) return toast.error("Add at least one check item");

    setBusy(true);
    try {
      if (editor.mode === "create") {
        const key = slugify(editor.key || label);
        if (!key) return toast.error("Couldn't derive a key from the name");
        await api.post("/audits/templates", { key, label, items });
        toast.success("Template created");
      } else {
        await api.put(`/audits/templates/${editor.originalKey}`, { key: editor.originalKey, label, items });
        toast.success("Template updated");
      }
      await reload();
      setEditor(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const del = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.label}"? Existing audits keep their checks.`)) return;
    try {
      await api.delete(`/audits/templates/${tpl.key}`);
      toast.success("Template deleted");
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="audit-templates-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {editor ? (editor.mode === "create" ? "New audit template" : `Edit template`) : "Audit templates"}
          </DialogTitle>
          <DialogDescription className="sr-only">Create, edit, delete or restore default audit templates.</DialogDescription>
        </DialogHeader>

        {!editor && (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-xs text-[#8a8476]">Owner-managed checklists used when starting a new audit.</p>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  data-testid="audit-template-restore-defaults"
                  onClick={async () => {
                    if (!window.confirm("Restore any missing default templates? Existing templates won't be touched.")) return;
                    try {
                      const { data } = await api.post("/audits/templates/restore-defaults");
                      if (data.restored === 0) toast.message("All defaults already present");
                      else toast.success(`Restored ${data.restored} default template${data.restored === 1 ? "" : "s"}`);
                      reload();
                    } catch (err) {
                      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
                    }
                  }}
                  className="border-[#1b3b36]/30 text-[#1b3b36] hover:bg-[#e5efe8] rounded-[4px] h-9 px-3"
                >
                  <RotateCcw className="w-3.5 h-3.5 mr-2" /> Restore defaults
                </Button>
                <Button data-testid="audit-template-new-button" onClick={startCreate} className="bg-[#1b3b36] hover:bg-[#2c5a53] text-white rounded-[4px] h-9 px-4">
                  <Plus className="w-4 h-4 mr-2" /> New template
                </Button>
              </div>
            </div>
            <div className="border border-[#e5e2d9] divide-y divide-[#efece6]" data-testid="audit-templates-list">
              {templates.length === 0 && (
                <div className="py-10 text-center text-sm text-[#8a8476]">No templates yet.</div>
              )}
              {templates.map((t, idx) => (
                <div key={t.key} data-testid={`audit-template-row-${idx}`} className="px-4 py-3 flex items-center gap-3 hover:bg-[#efece6]/40">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{t.label}</span>
                      {t.is_default && (
                        <span className="text-[10px] uppercase tracking-[0.18em] text-[#4e7b5b] border border-[#4e7b5b]/40 px-1.5 py-0.5">default</span>
                      )}
                      <span className="text-[10px] uppercase tracking-[0.18em] text-[#8a8476] font-mono">{t.key}</span>
                    </div>
                    <div className="text-xs text-[#57534e] mt-1">{t.items.length} check{t.items.length === 1 ? "" : "s"}</div>
                  </div>
                  <button data-testid={`audit-template-edit-${idx}`} onClick={() => startEdit(t)} className="text-[#8a8476] hover:text-[#1b3b36]" aria-label="Edit"><Pencil className="w-4 h-4" /></button>
                  <button data-testid={`audit-template-delete-${idx}`} onClick={() => del(t)} className="text-[#8a8476] hover:text-[#d94f3d]" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {editor && (
          <div className="space-y-4">
            <Field label="Template name">
              <Input
                data-testid="audit-template-label-input"
                value={editor.label}
                onChange={(e) => setEditor((s) => ({ ...s, label: e.target.value }))}
                placeholder="e.g. Daily kitchen open"
                className="bg-white rounded-[4px]"
              />
            </Field>
            {editor.mode === "create" && (
              <Field label="Key (slug)">
                <Input
                  data-testid="audit-template-key-input"
                  value={editor.key}
                  onChange={(e) => setEditor((s) => ({ ...s, key: e.target.value }))}
                  placeholder={slugify(editor.label) || "auto_from_name"}
                  className="bg-white rounded-[4px] font-mono text-sm"
                />
                <div className="text-[10px] text-[#8a8476] mt-1">lowercase letters, numbers and underscores. Auto-generated if left blank.</div>
              </Field>
            )}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] uppercase tracking-[0.18em] text-[#8a8476]">Check items</span>
                <button type="button" data-testid="audit-template-add-item" onClick={addItem} className="text-[11px] uppercase tracking-[0.18em] text-[#1b3b36] hover:underline inline-flex items-center gap-1">
                  <Plus className="w-3 h-3" /> Add item
                </button>
              </div>
              <div className="space-y-2" data-testid="audit-template-items">
                {editor.items.map((val, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input
                      data-testid={`audit-template-item-${idx}`}
                      value={val}
                      onChange={(e) => setItem(idx, e.target.value)}
                      placeholder={`Check #${idx + 1}`}
                      className="bg-white rounded-[4px]"
                    />
                    <button
                      type="button"
                      data-testid={`audit-template-item-remove-${idx}`}
                      onClick={() => removeItem(idx)}
                      className="text-[#8a8476] hover:text-[#d94f3d]"
                      aria-label="Remove item"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditor(null)} className="rounded-[4px]" data-testid="audit-template-cancel">
                Cancel
              </Button>
              <Button type="button" onClick={save} disabled={busy} data-testid="audit-template-save" className="bg-[#1b3b36] hover:bg-[#2c5a53] text-white rounded-[4px]">
                {busy ? "Saving…" : editor.mode === "create" ? "Create template" : "Save template"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
